import React, { useState } from "react";
import axios from "axios";
import { Alert, Form, Modal, Spinner, Button } from "react-bootstrap";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineCloseCircle, AiOutlineCheckCircle } from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import { hearAboutUs } from "../dropdown-options/hear-about-us";
import { knowYouBetter } from "../dropdown-options/know-you-better";
import { inquiry } from "../dropdown-options/inquiry";
import "./dashboard copy.css";
import "../prnewswire/dashboard.css";
import HearAboutUsForm from "../forms/hear-about-us-form";
import { hearAboutUsFieldConfig } from "../forms/configs";

/**
 * CardAnnouncement Component
 * 
 * Displays an announcement card on the dashboard with a "Get in touch" modal form.
 * Collects user contact information, inquiry type, hearing source, and detailed feedback.
 * Submits data via axios POST request to backend API endpoint.
 * 
 * @param {Object} props - Component props
 * @param {string} props.date - Date to display in the announcement
 * @returns {JSX.Element} Announcement card with modal form
 */
const CardAnnouncement = ({ date }) => {
  // State to control alert visibility
  const [alert, setAlert] = useState(false);

  // State to store alert message content
  const [message, setMessage] = useState();

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // State to control "Get in touch" modal visibility
  const [showCard, setShowCard] = useState(false);

  // Display the "Get in touch" modal form
  function displayCard() {
    setShowCard(true);
  }

  // Hide the "Get in touch" modal form
  function hideCard() {
    setShowCard(false);
  }

  // Form initial state with all contact and inquiry fields
  // Includes basic contact info (email, name), inquiry type, hearing source,
  // and dynamic fields for each hearing source with platform/description/date/url details
  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    knowYouBetter: '',
    hearAboutUs: '',
    inquiry: '',
    others: '',
    tellUsMore: '',
    id: 13, // id of get in touch contacts list

    // hearAboutUs Fields
    "emailDescription": "",
    "emailDate": "",
    "emailUrl": "",
    "socialMediaPlatform": "",
    "socialMediaDescription": "",
    "socialMediaPaid": "",
    "socialMediaDate": "",
    "socialMediaUrl": "",
    "searchEnginePlatform": "",
    "searchEngineOther": "",
    "searchPaid": "",
    "searchDescription": "",
    "searchDate": "",
    "searchUrl": "",
    "adPlatform": "",
    "adDescription": "",
    "adDate": "",
    "adUrl": "",
    "wordOfMouthDescription": "",
    "wordOfMouthDate": "",
    "wordOfMouthFrom": "",
    "govDescription": "",
    "govReceivedMethod": "",
    "govDate": "",
    "govUrl": "",
    "recommendWhoName": "",
    "recommendHow": "",
    "recommendDate": "",
    "recommendContact": "",
    "researchPlatform": "",
    "researchDescription": "",
    "researchDate": "",
    "researchUrl": "",
    "newsDescription": "",
    "newsDate": "",
    "newsUrl": "",
    "eventLocation": "",
    "eventDescription": "",
    "eventDate": "",
    "eventUrl": "",
    "schoolName": "",
    "schoolDescription": "",
    "schoolDate": "",
    "schoolUrl": "",
    "podcastPlatform": "",
    "podcastDescription": "",
    "podcastDate": "",
    "podcastUrl": "",
    "otherInfo": "",
    "otherUrl": ""
  }

  // State to manage form submission data in "Get in touch" modal
  const [subscription, setSubscription] = useState(initialValues);

  /**
   * Handle form input changes in the "Get in touch" modal
   * Updates the subscription state when user types in any form field
   * 
   * @param {Object} e - Event object from form input
   */
  function handleChange(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    // Create a copy of form state with the updated field value
    let subscriptionCopy = { ...subscription };

    subscriptionCopy[name] = value;
    setSubscription({ ...subscriptionCopy });
  }

  /**
   * Submit the "Get in touch" form via HTTP POST request to MongoDB API
   * Shows loading message during submission, then success or error message
   * Resets form to initial values on successful submission
   * 
   * @param {Object} e - Event object from form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    hideCard();

    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>
    );

    try {
      const options = await axios.request({
        method: "POST",
        url: "https://data.mongodb-api.com/app/flipidea-3-pputk/endpoint/saveContactToBrevo",
        data: subscription,
        headers: { "Content-Type": "application/json" },
      });

      setAlert(true);
      setMessage(
        <div style={{ paddingTop: "10px" }}>
          <p style={{ color: "#5aa469" }}>
            <AiOutlineCheckCircle className="alert-icon" /> Thank you for
            reaching out!
          </p>
          <p style={{ marginLeft: "28px" }}>We'll be in touch shortly.</p>
        </div>
      );

      setSubscription(initialValues);

    } catch (error) {
      setAlert(true);
      setMessage(
        <div style={{ paddingTop: "10px" }}>
          <p style={{ fontSize: "16px", color: "red" }}>
            <AiOutlineCloseCircle className="alert-icon" /> Error
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            An unknown error occurred. Please try again. If error persists,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
      );
    }
  };

  return (
    <>
      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <p className="dashboardCardHeader">
        <Form.Select>
          <option>Announcement</option>
        </Form.Select>
      </p>

      <hr />

      <p className="dashboardCardData">Feedback</p>

      <div className="dashboardDivScrollable">
        <p className="dashboardCardText">
          If you have any feedback, or want to submit a request, please
          <a href id="letusknow" onClick={(e) => displayCard()}>
            {" "}
            let us know
          </a>
          .
          <ul>
            <li>
              Due to large amount of data, our real-time analytics may take up
              to 1 min to compute and show results.
            </li>
            <li>
              As we use advanced analytics and technologies to analyze inputs
              and queries, we strive to reduce waiting time.
            </li>
            <li>
              Some of our data may not be accurately labelled or sorted because
              our data preprocessing are semi-automated.
            </li>
            <li>
              Under no obligation, roiquant (roy-kwant) offers free subscription
              ($0 forever) to some of{" "}
              <a
                href="https://www.roiquant.com/products"
                target="_blank"
                rel="noopener noreferrer"
              >
                our products
              </a>{" "}
              with limited functionality.
            </li>
            <li>
              <a
                href="https://viewpoints.roiquant.com/disclaimer/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Disclaimer
              </a>
              : The development of our products and technologies are subject to
              continuous R&D and improvement.
            </li>
          </ul>
        </p>
      </div>

      <div className="dashboardBottomDiv">
        <br />

        <p className="dashboardMinorText">Updated: {date}</p>
      </div>

      <Modal
        className="getintouchformdiv"
        show={showCard}
        onHide={hideCard}
        backdrop="static"
        fullscreen="sm-down"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          className="getintouchformheader"
          style={{ borderBottom: "none" }}
        />
        <Modal.Body className="getintouchformbody">
          <div>
            <Form onSubmit={(e) => handleSubmit(e)}>
              <div
                style={{ width: "100%", display: "flex", flexDirection: "row" }}
              >
                <Form.Group className="inputgroup"
                  controlId="formBasicFirstName"
                  style={{ width: "50%", paddingRight: "1.5%" }}
                >
                  First name <sup className="text-danger">*</sup>
                  <Form.Control
                    onChange={handleChange}
                    required
                    maxlength="20"
                    className="createaccinputbox"
                    type="text"
                    value={subscription.firstName}
                    name="firstName"
                    placeholder="Enter your first name"
                  />
                </Form.Group>

                <Form.Group className="inputgroup"
                  controlId="formBasicLastName"
                  style={{ width: "50%" }}
                >
                  Last name <sup className="text-danger">*</sup>
                  <Form.Control
                    onChange={handleChange}
                    required
                    maxlength="20"
                    className="createaccinputbox"
                    type="text"
                    value={subscription.lastName}
                    name="lastName"
                    placeholder="Enter your last name"
                  />
                </Form.Group>
              </div>
              <Form.Group className="inputgroup" controlId="formBasicEmail">
                Email <sup className="text-danger">*</sup>
                <Form.Control
                  onChange={handleChange}
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  required
                  className="createaccinputbox"
                  type="email"
                  value={subscription.email}
                  name="email"
                  placeholder="Enter your email address"
                />
              </Form.Group>
              We would like to know you better <sup className="text-danger">*</sup>
              <Form.Group className="inputgroup" controlId="formBasicAbout">
                <Form.Select
                  onChange={handleChange}
                  required
                  className="createaccformbox"
                  value={subscription.knowYouBetter}
                  name="knowYouBetter"
                  placeholder=""
                >
                  <option></option>
                  {knowYouBetter.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Hear About Us */}
              <HearAboutUsForm
                subscription={subscription}
                setSubscription={setSubscription}
                handleChange={handleChange}
                // errors={errors}
                // touched={touched}
                isLoading={false}
              />


              What is your inquiry about ?<sup className="text-danger">*</sup>
              <Form.Group className="inputgroup" controlId="formBasicInquiry">
                <Form.Select
                  onChange={handleChange}
                  required
                  className="createaccformbox"
                  value={subscription.inquiry}
                  name="inquiry"
                  placeholder=""
                >
                  <option></option>
                  {inquiry.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {/* Function to handle if user select "Other" */}
              {subscription.inquiry === "Other" ? (
                <Form.Group className="inputgroup" controlId="formBasicOther">
                  <Form.Control
                    onChange={handleChange}
                    required
                    className="createaccinputbox"
                    type="text"
                    value={subscription.others}
                    name="others"
                    placeholder=" Enter your subject of inquiry"
                  />
                </Form.Group>
              ) : (
                ""
              )}
              <Form.Group className="inputgroup"
                controlId="formBasicTellUsMore"
                style={{ width: "100%" }}
              >
                Tell us more <sup className="text-danger">*</sup>
                <Form.Control
                  as="textarea"
                  rows={3}
                  onChange={handleChange}
                  required
                  className="createaccformbox"
                  value={subscription.tellUsMore}
                  name="tellUsMore"
                  placeholder="How can we help you?"
                />
              </Form.Group>
              <div style={{ textAlign: "center" }}>
                {subscription.email === "" ||
                  subscription.firstName === "" ||
                  subscription.lastName === "" ||
                  subscription.knowYouBetter === "" ||
                  subscription.hearAboutUs == "" ||
                  (
                    hearAboutUsFieldConfig[subscription.hearAboutUs]?.some(field => {
                      if (!field.required) return false;
                      const value = subscription[field.name];
                      return value === "" || value === undefined || value === null;
                    })
                  )
                  ||
                  subscription.inquiry === "" ||
                  (subscription.inquiry === "Other" &&
                    subscription.others === "") ||
                  subscription.tellUsMore === "" ? (
                  <button className="orderbutton glintanimation" disabled>
                    Get in touch
                  </button>
                ) : (
                  <button
                    className="orderbutton glintanimation"
                    onClick={handleSubmit}
                  >
                    Get in touch
                  </button>
                )}
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CardAnnouncement;
